import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiRequest, ApiError } from '../../services/apiClient';
import './NotificationsBell.css';

/**
 * Workforce Notifications PR-3 — header bell.
 *
 * Polls /api/notifications/unread-count every 60 seconds. The
 * endpoint sets a 10s Cache-Control so concurrent polls coalesce
 * behind any proxy. On user-click the bell deep-links to the
 * /notifications inbox.
 *
 * The component is intentionally minimal — the full inbox
 * (filters, pagination, acknowledgement) lives at /notifications.
 *
 * Tenant + user scope: the API layer enforces that the count is
 * scoped to req.user.companyId AND req.user.id. The bell makes
 * no claim about cross-tenant data and never accepts a companyId
 * prop.
 */
const DEFAULT_POLL_MS = 60000;
const MAX_DISPLAY = 9;

function NotificationsBell({
  onNavigate = null,
  pollIntervalMs = DEFAULT_POLL_MS,
  initialCount = null,
  disabled = false
}) {
  const [unreadCount, setUnreadCount] = useState(
    Number.isFinite(initialCount) ? initialCount : 0
  );
  const [hasError, setHasError] = useState(false);
  const aliveRef = useRef(true);

  const fetchCount = useCallback(async () => {
    if (disabled) return;
    try {
      const data = await apiRequest('/api/notifications/unread-count');
      if (!aliveRef.current) return;
      setUnreadCount(Number(data?.count || 0));
      setHasError(false);
    } catch (err) {
      if (!aliveRef.current) return;
      if (err instanceof ApiError && err.status === 401) {
        // Auth lost; let the auth shell handle re-login.
        setHasError(false);
        return;
      }
      setHasError(true);
    }
  }, [disabled]);

  useEffect(() => {
    aliveRef.current = true;
    fetchCount();
    if (disabled || !pollIntervalMs) return () => { aliveRef.current = false; };
    const id = setInterval(fetchCount, pollIntervalMs);
    return () => {
      aliveRef.current = false;
      clearInterval(id);
    };
  }, [fetchCount, pollIntervalMs, disabled]);

  const handleClick = useCallback(() => {
    if (typeof onNavigate === 'function') {
      onNavigate('/notifications');
    }
  }, [onNavigate]);

  const displayCount = unreadCount > MAX_DISPLAY ? `${MAX_DISPLAY}+` : String(unreadCount);
  const ariaLabel = unreadCount > 0
    ? `Notifications — ${unreadCount} unread`
    : 'Notifications — none unread';

  return (
    <button
      type="button"
      className={`notifications-bell ${unreadCount > 0 ? 'notifications-bell--has-unread' : ''} ${hasError ? 'notifications-bell--errored' : ''}`}
      onClick={handleClick}
      aria-label={ariaLabel}
      data-testid="notifications-bell"
      disabled={disabled}
    >
      <span className="notifications-bell-icon" aria-hidden>
        <i className="fas fa-bell" />
      </span>
      {unreadCount > 0 ? (
        <span
          className="notifications-bell-badge"
          data-testid="notifications-bell-badge"
        >
          {displayCount}
        </span>
      ) : null}
    </button>
  );
}

export default NotificationsBell;
